import {RequestMethod} from '@gl/lit/common/constants/index.js';
import type {
  MatchFieldResponse,
  DestinationPlatformsResponse,
} from '@gl/types/destinations.js';
import type {FetcherParams} from '@gl/lit/utils/http.js';
import {fetcher} from '@gl/lit/utils/http.js';
import type {
  CampaignTriggersErrorResponse,
  AllowedSenderEmailsResponse,
  CampaignTemplatesResponse,
  EmailTemplate,
} from '@gl/lit/types/journey/paid_media_export.js';
import {isPlainObject} from '@gl/lit/utils/index.js';
import type {AnyObject} from '@gl/lit/types/general.js';

type SendTestPostBody = {
  from: string;
  to: string;
  variables: string[];
};

class DestinationsService {
  async getMatchFields() {
    try {
      const matchFieldEndpoint = '/api/destination_match_fields';
      return await fetcher<MatchFieldResponse>(matchFieldEndpoint, {
        method: RequestMethod.GET,
        responseParser: 'json',
      });
    } catch (e) {
      console.error(e);
      throw new Error('unable to get match fields');
    }
  }

  async getDestinationPlatforms(): Promise<DestinationPlatformsResponse> {
    try {
      const endpoint = '/destination_platforms.json';
      return await fetcher<DestinationPlatformsResponse>(endpoint, {
        method: RequestMethod.GET,
        responseParser: 'json',
      });
    } catch (e) {
      console.error(e);
      throw new Error('unable to get destination platforms');
    }
  }

  async getAllowedSenderEmails(
    destinationConnectionID: string
  ): Promise<AllowedSenderEmailsResponse> {
    return await this.buildFetcher<AllowedSenderEmailsResponse>(
      `/api/v2/destination_connections/${destinationConnectionID}/campaign_triggers/senders`
    );
  }

  async getCampaignTemplates(
    destinationConnectionID: string
  ): Promise<CampaignTemplatesResponse> {
    return await this.buildFetcher<CampaignTemplatesResponse>(
      `/api/v2/destination_connections/${destinationConnectionID}/campaign_triggers/templates`
    );
  }

  async getCampaignTemplateDetails(
    destinationConnectionID: string,
    templateID: string
  ): Promise<EmailTemplate> {
    return await this.buildFetcher<EmailTemplate>(
      `/api/v2/destination_connections/${destinationConnectionID}/campaign_triggers/templates/${templateID}`
    );
  }

  async sendTestEmail(
    destinationConnectionID: string,
    templateID: string,
    body: SendTestPostBody
  ): Promise<AnyObject> {
    const path = `/api/v2/destination_connections/${destinationConnectionID}/campaign_triggers/templates/${templateID}/send_test`;

    return await this.buildFetcher<AnyObject>(path, RequestMethod.POST, {body});
  }

  private async buildFetcher<ResponseType>(
    path: string,
    method = RequestMethod.GET,
    fetcherParams: Omit<FetcherParams, 'method'> = {}
  ): Promise<ResponseType> {
    if (!path) {
      throw new Error('Unable to fetch - empty params');
    }

    const url = new URL(path, location.origin);
    try {
      return await fetcher<ResponseType>(url.toString(), {
        ...fetcherParams,
        method,
      });
    } catch (err: unknown) {
      console.error(err);

      let errorMessage = err;
      if (isResponseError(err)) {
        errorMessage = new Error(err.error);
      }

      throw errorMessage;
    }
  }
}

function isResponseError(err: unknown): err is CampaignTriggersErrorResponse {
  if (!isPlainObject(err)) return false;
  return 'error' in err;
}

export const destinationsService = new DestinationsService();
